'use client';

import { Dispatch, SetStateAction, useCallback, useState } from 'react';

export type HookProps<T = unknown> = {
  isVisible: boolean;
  data: T | null;
  toggle: () => void;
  hide: () => void;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  show: (data?: T) => void;
};

type Props = {
  open?: boolean;
  onShow?: () => void;
};

/**
 *  hook to keep track of a modal state.
 */
const useModal = <T = unknown>(props: Props = {}): HookProps<T> => {
  const { open, onShow } = props;

  const [isVisible, setIsVisible] = useState(!!open);
  const [data, setData] = useState<T | null>(null);

  const show = useCallback(
    (_data?: T): void => {
      onShow?.();
      setIsVisible(true);
      _data && setData(_data);
    },
    [onShow],
  );

  const hide = useCallback((): void => {
    setIsVisible(false);
    setData(null);
  }, []);

  const toggle = (): void => setIsVisible((prev) => !prev);

  return { show, hide, toggle, isVisible, setIsVisible, data };
};

export default useModal;
